import { Classes, Icon, IconName, Intent } from '@clarion-blueprint-shim';
import { ReactElement, RefObject } from 'react';
import {
  composeRenderProps,
  Button as RACButton,
  ButtonProps as RACButtonProps,
  TooltipTrigger,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { cx } from '../util';
import { ClarionSpinner } from './/ClarionSpinner';
import { ClarionTooltip } from './ClarionTooltip';
import { focusRing } from './utils';

export type ClarionButtonOrLinkProps = {
  tooltip?: string;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  variant?: 'minimal' | 'outlined' | 'solid' | 'selected';
  intent?: Intent;
  size?: 'small' | 'medium' | 'large';
  icon?: IconName | ReactElement<any>;
  endIcon?: IconName | ReactElement<any>;
  loading?: boolean;
  fill?: boolean;
  textAlign?: 'left' | 'center' | 'right';
};

export interface ClarionButtonProps extends ClarionButtonOrLinkProps, RACButtonProps {
  ref?: RefObject<HTMLButtonElement>;
  onClick?: (e: any) => void;
}

export const buttonStyles = tv({
  extend: focusRing,
  base: `${Classes.BUTTON} transition forced-colors:bg-[ButtonFace] forced-colors:border-[solid_ButtonBorder_1px]!`,
  variants: {
    variant: {
      solid: '',
      minimal: Classes.MINIMAL,
      outlined: Classes.OUTLINED,
      selected: [
        Classes.MINIMAL,
        'bg-purple-100/50! dark:bg-purple-100/10! dark:text-purple-100! forced-colors:bg-[Highlight]!',
        'hover:bg-purple-100/60! dark:hover:bg-purple-900/40!',
      ],
    },
    intent: {
      none: '',
      primary: Classes.INTENT_PRIMARY,
      success: Classes.INTENT_SUCCESS,
      warning: Classes.INTENT_WARNING,
      danger: Classes.INTENT_DANGER,
    },
    size: {
      small: Classes.SMALL,
      medium: '',
      large: Classes.LARGE,
    },
    loading: {
      true: Classes.DISABLED,
    },
    fill: {
      true: Classes.FILL,
    },
    isDisabled: {
      true: Classes.DISABLED,
    },
    textAlign: {
      left: Classes.ALIGN_LEFT,
      center: '',
      right: Classes.ALIGN_RIGHT,
    },
  },
  defaultVariants: {
    variant: 'solid',
    intent: 'none',
    size: 'medium',
    textAlign: 'center',
  },
});

// Wrapper around RAC Button with a Blueprint-like API and styles
export const ClarionButton = ({
  ref,
  icon,
  endIcon,
  children,
  tooltip,
  tooltipPlacement = 'bottom',
  onClick,
  ...props
}: ClarionButtonProps) => {
  const button = (
    <RACButton
      ref={ref}
      // If tooltip is provided, use it as the aria-label (will be overridden by explicit aria-label in the ...props spread below)
      aria-label={tooltip}
      onPress={onClick}
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        buttonStyles({ ...renderProps, ...props, className }),
      )}
    >
      {composeRenderProps(children, (children) => (
        <>
          {props.loading ? <ClarionSpinner size={16} /> : icon && <Icon icon={icon} />}
          {children && <span className={cx(Classes.BUTTON_TEXT, 'min-w-0')}>{children}</span>}
          {endIcon && <Icon icon={endIcon} />}
        </>
      ))}
    </RACButton>
  );

  if (tooltip) {
    return (
      <TooltipTrigger delay={500}>
        {button}
        <ClarionTooltip placement={tooltipPlacement}>{tooltip}</ClarionTooltip>
      </TooltipTrigger>
    );
  }

  return button;
};
